/**
 * Shared section-level styled components and style objects.
 * ExploreTools, CoursesSection, and NewsSection all use a SectionWrapper
 * with the same structure but different padding — use createSectionWrapper()
 * so padding is configurable without duplicating the rest.
 */
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';

type SpacingPair = [number, number];

/** Factory that creates a SectionWrapper with the given vertical padding. */
export const createSectionWrapper = (
  padding: SpacingPair,
  paddingMd: SpacingPair,
) =>
  styled(Box)(({ theme }: { theme: Theme }) => ({
    backgroundColor: 'transparent',
    background: 'transparent',
    padding: theme.spacing(...padding),
    position: 'relative',
    width: '100%',
    transition: 'all 0.3s ease-in-out',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(...paddingMd),
    },
  }));

/** Standard section wrapper — used by ExploreTools and CoursesSection. */
export const SectionWrapper = createSectionWrapper([3, 0], [2.5, 0]);

/** Wider-padded section wrapper — used by NewsSection. */
export const NewsSectionWrapper = createSectionWrapper([4, 0], [3, 0]);

/** Common Container sx used identically across all homepage sections. */
export const sectionContainerSx = {
  px: { xs: 2, sm: 2, md: 2, lg: 2, xl: 2 },
  width: '100%',
  maxWidth: '100%',
} as const;
